import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { selectFeatureFlagUnresolvedItems } from '../config/config.selector';
import { selectSessionId } from '../sessionBoundary/sessionBoundary.selectors';
import {
  selectIsStaffIntervention,
  selectRestaurantStaffIntervention,
} from '../../../selectors/message';
import { RestaurantStaffInterventionStatus } from '../../../reducers/messagingSlice';

const selectAI = (state: RootState) => state.ai;
const selectIsAIAutoMode = createSelector(selectAI, (ai) => ai.isAIAutoMode);
const selectIsAIEscalation = createSelector(
  selectAI,
  (ai) => ai.isAIEscalation
);
const selectAIStatus = createSelector(selectAI, (ai) => ai.aiStatus);

const selectAIEscalationReason = createSelector(
  selectAI,
  (ai) => ai.aiEscalationReason
);
const selectIsAIEscalatedOnCarEnter = createSelector(
  selectAI,
  (ai) => ai.isAIEscalatedOnCarEnter
);
const selectRequiresTakeover = createSelector(
  selectAI,
  (ai) => ai.requiresTakeover
);

// HITL 3.0 (ai_session_state) — Pulse card fields.
const selectAITurn = createSelector(selectAI, (ai) => ai.turn);
const selectAISentimentLabel = createSelector(
  selectAI,
  (ai) => ai.sentimentLabel
);
const selectAIReusedAnswers = createSelector(
  selectAI,
  (ai) => ai.reusedAnswers
);
const selectAIExpressModeActive = createSelector(
  selectAI,
  (ai) => ai.expressModeActive
);

/**
 * HITL-1650 — the blinking red "take over now" band, as one predicate.
 *
 * This was four terms computed inline in `CustomerPulse`, which was fine while
 * the blink was the only thing that cared. It is not any more: the auto-switch
 * into `Intercept` has to fire on EXACTLY the state the agent is looking at, or
 * the console tells them one thing and does another. Two copies of a four-term
 * condition drift; one selector cannot.
 *
 * Each term, and why it is in here:
 *
 *   `unresolvedItems`   the feature that raises the takeover request at all
 *   `requiresTakeover`  the AI's own "I need a human" flag, sticky to session end
 *   `sessionId`         gated on the SESSION, never on car presence — car_exit
 *                       with a pending order is normal, and resetting AI control
 *                       state on it caused a prod incident (dropped order + cart
 *                       revert)
 *   `isAIAutoMode`      the alert is for the window BEFORE the agent has acted;
 *                       once they intercept for real it stops asking
 *   staff intervention  a human already owns the order, so the band drops the
 *                       blink for the static hand-off red
 *
 * Read-only on all five. Nothing here mutates `isAIAutoMode`, which is the rule
 * that MR !1534 exists to enforce.
 */
const selectShowsTakeoverAlert = createSelector(
  selectFeatureFlagUnresolvedItems,
  selectRequiresTakeover,
  selectSessionId,
  selectIsAIAutoMode,
  selectIsStaffIntervention,
  selectRestaurantStaffIntervention,
  (
    unresolvedItemsEnabled,
    requiresTakeover,
    sessionId,
    isAIAutoMode,
    isStaffIntervention,
    restaurantStaffIntervention
  ) =>
    !!unresolvedItemsEnabled &&
    !!requiresTakeover &&
    !!sessionId &&
    !!isAIAutoMode &&
    !isStaffIntervention &&
    restaurantStaffIntervention === RestaurantStaffInterventionStatus.initial
);

export {
  selectAIStatus,
  selectIsAIAutoMode,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectIsAIEscalatedOnCarEnter,
  selectRequiresTakeover,
  selectShowsTakeoverAlert,
  selectAITurn,
  selectAISentimentLabel,
  selectAIReusedAnswers,
  selectAIExpressModeActive,
};
